import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { toast } from "react-toastify";
import { apiClient } from "../../api/httpClient";
import { buildBondStatusPatchPayload } from "../../globals/utils/bondStatusPatchUtils";
import { exportToExcel } from "../../globals/utils/excelExport";
import { setArchivesFilters, setArchivesPage, resetArchivesState } from "../../store/slices/bondArchivesSlice";
import { BOND_TYPE_TO_SUBCATEGORIES, sortBondRows } from "../SHARED/bondPageUtils";
import {
  getCancellationInstructionsList,
  getEndorsementInstructions,
} from "../Law Firm User/helpers/lawFirmBondsCancellationCopy";
import {
  applyBondArchivesFilters,
  normalizePaymentStatusKey,
  formatPaymentStatusExportLabel,
  getPaymentStatusSearchText,
} from "../Law Firm User/helpers/lawFirmBondsHelpers";
import { getBondUpdateEndpoint } from "./bondStatusUpdateHelpers";
import { useBondEndorsementDialogState } from "./useBondEndorsementDialogState";
import useBondEndorsementSubmitHandlers from "./useBondEndorsementSubmitHandlers";
import useBondCancelDeleteHandlers from "./useBondCancelDeleteHandlers";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const statusOptions = ["Active", "Approved", "Rejected", "To Be Cancelled", "Cancelled"];

export default function useBondArchives() {
  const dispatch = useDispatch();
  const savedFilters = useSelector((state) => state.bondArchives?.filters) || {
    bondNumber: [],
    bondType: [],
    bondSubCategory: [],
    principal: [],
    professionalGuardian: [],
    estate: [],
    lawFirm: [],
    attorney: [],
    internalUser: [],
    status: [],
    payment_status: "",
    startDate: null,
    endDate: null,
  };
  const savedPage = useSelector((state) => state.bondArchives?.page ?? 0);

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(savedFilters);

  const [bondTypes, setBondTypes] = useState([]);
  const [lawFirms, setLawFirms] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [professionalGuardians, setProfessionalGuardians] = useState([]);
  const [estates, setEstates] = useState([]);
  const [principals, setPrincipals] = useState([]);
  const [allBonds, setAllBonds] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery] = useState(""); // setSearchQuery removed - search UI not implemented yet
  const [page, setPage] = useState(savedPage);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBondToCancel, setSelectedBondToCancel] = useState(null);
  const [selectedBondToLiberty, setSelectedBondToLiberty] = useState(null);
  const [cancellationFile, setCancellationFile] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBondToDelete, setSelectedBondToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMarkingCancellationComplete, setIsMarkingCancellationComplete] = useState(false);
  const [isMarkingToBeCancelled, setIsMarkingToBeCancelled] = useState(false);
  const [isMarkingLibertyComplete, setIsMarkingLibertyComplete] = useState(false);
  const [libertyConfirmDialogOpen, setLibertyConfirmDialogOpen] = useState(false);
  const [bypassHistoryDialogOpen, setBypassHistoryDialogOpen] = useState(false);
  const [bypassHistoryList, setBypassHistoryList] = useState([]);
  const [bypassHistoryLoading, setBypassHistoryLoading] = useState(false);
  const [bypassHistorySearch, setBypassHistorySearch] = useState("");
  const [paymentOverrideHistoryOpen, setPaymentOverrideHistoryOpen] = useState(false);
  const [paymentOverrideHistoryList, setPaymentOverrideHistoryList] = useState([]);
  const [paymentOverrideHistoryLoading, setPaymentOverrideHistoryLoading] = useState(false);
  const [paymentOverrideHistorySearch, setPaymentOverrideHistorySearch] = useState("");

  // (Charts removed; this page only handles table and actions)

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filters.lawFirm.length > 0) {
      const firmIds = filters.lawFirm.map((f) => f.value);
      Promise.all(
        firmIds.map((id) => apiClient(`/lawfirm-user-location/${id}`, "GET"))
      ).then((responses) => {
        const merged = responses.flat()
          .filter((a) => a.Position === "Attorney")
          .map((a) => ({
            label: a.lawfirm_user_name,
            value: a.lawfirm_user_id,
          }));
        setAttorneys(merged);
      });
    } else {
      setAttorneys([]);
    }
  }, [filters.lawFirm]);

  const fetchInitialData = async (overrideFilters = null) => {
    setLoading(true);
    try {
      const [bt, lf, est, pr, iu, bonds, pg] = await Promise.all([
        apiClient("/bondtypes/", "GET"),
        apiClient("/list-lawfirms/", "GET"),
        apiClient("/estates", "GET"),
        apiClient("/principals", "GET"),
        apiClient("/internal-user", "GET"),
        apiClient("/archive-bonds", "GET"),
        apiClient("/get-all-professional-guardians", "GET"),
      ]);

      setBondTypes(
        bt
          .filter((b) => b.type !== "Guardian-Minor")
          .map((b) => ({ label: b.type, value: b.type }))
      );
      setLawFirms(lf.map((f) => ({ label: f.name, value: f.lawfirm_id })));
      setEstates(est.map((e, index) => ({ 
        label: e.name, 
        bondType: e.bond_type, 
        value: `${e.name}-${e.bond_type}-${index}` 
      })));
      setPrincipals(pr.map((p, index) => ({ 
        label: p.name, 
        bondType: p.role, 
        value: `${p.name}-${p.role}-${index}` 
      })));
      setInternalUsers(
        iu.data.map((u) => ({ label: u.full_name, value: u.internal_user_id }))
      );
      const formattedGuardians = (pg?.data || []).map((guardian) => ({
        label: guardian.full_name,
        value: guardian.pg_id,
      }));
      setProfessionalGuardians(formattedGuardians);

      const formatted = bonds.map((b) => ({
        bond_id: b.bond_id,
        bond_number: b.bond_number || "", // <-- NEW FIELD
        bond_type: b.bond_type,
        bond_sub_category: b.bond_sub_category,
        principals: Array.isArray(b.principals) ? b.principals : [],
        estate_name: b.name,
        law_firm_name: b.law_firm_name,
        attorney_name: b.law_firm_user_name,
        underwriting_status: b.underwriting_status || "-",
        bond_amount: b.bond_amount || "0.00",
        managed_by: Array.isArray(b.managed_by) ? b.managed_by.map((u) => u.full_name) : [],
        status: b.status || "Active",
        payment_status: b.payment_status ?? "",
        created_at: b.created_at,
      }));

      setAllBonds(formatted);
      // Re-apply filters; allow override to avoid using stale state on manual refresh
      const f = overrideFilters ?? filters ?? savedFilters;
      const result = await applyBondArchivesFilters(formatted, f);

      setFilteredData(result);
    } catch (err) {
      console.error("Error loading data", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  const handleExport = () => {
    const dataToExport = filteredData.map((b) => ({
      "Bond Number": b.bond_number,
      "Bond Type": b.bond_type,
      "Bond Sub Category": b.bond_sub_category,
      Principals: Array.isArray(b.principals) ? b.principals.join(", ") : (b.principals || ""),
      "Estate Name": b.estate_name,
      "Law Firm": b.law_firm_name,
      Attorney: b.attorney_name,
      "Managed By": Array.isArray(b.managed_by) ? b.managed_by.join(", ") : (b.managed_by || ""),
      "Bond Amount": b.bond_amount,
      Status: b.status,
      "Payment Status": formatPaymentStatusExportLabel(b.payment_status),
      "Created At": dayjs(b.created_at).format("DD MMM YYYY"),
    }));
    exportToExcel(dataToExport, "Bonds", "bond_archives_export");
  };

  const applyFilters = async () => {
    const result = await applyBondArchivesFilters(allBonds, filters);
    setFilteredData(result);
    setFilterDialogOpen(false);
    setPage(0);
    dispatch(setArchivesFilters(filters));
    dispatch(setArchivesPage(0));
  };

  const clearFilters = () => {
    const cleared = {
      bondNumber: [],
      bondType: [],
      bondSubCategory: [],
      principal: [],
      professionalGuardian: [],
      estate: [],
      lawFirm: [],
      attorney: [],
      internalUser: [],
      status: [],
      payment_status: "",
      startDate: null,
      endDate: null,
    };
    setFilters(cleared);
    dispatch(setArchivesFilters(cleared));
    setPage(0);
    dispatch(setArchivesPage(0));
  };

  const dispatchArchiveFilters = useCallback(
    (next) => {
      setFilters(next);
      dispatch(setArchivesFilters(next));
    },
    [dispatch]
  );

  const bondNumberOptionsArchive = useMemo(
    () =>
      [...new Set(allBonds.map((b) => b.bond_number).filter(Boolean))].map((num) => ({
        label: num,
        value: num,
      })),
    [allBonds]
  );

  const selectedBondTypes = filters.bondType.map((b) => b.value);

  const availableSubcategories = selectedBondTypes.flatMap(
    (type) => BOND_TYPE_TO_SUBCATEGORIES[type] || []
  );

  const availablePrincipals = selectedBondTypes.length
    ? principals.filter((p) => selectedBondTypes.includes(p.bondType))
    : principals;

  const availableEstates = selectedBondTypes.length
    ? estates.filter((e) => selectedBondTypes.includes(e.bondType))
    : estates;

  const filteredRows = sortBondRows(
    filteredData.filter((r) => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        (Array.isArray(r.principals) && r.principals.some((p) => p.toLowerCase().includes(query))) ||
        r.bond_number?.toLowerCase().includes(query) ||
        r.estate_name?.toLowerCase().includes(query) ||
        r.law_firm_name?.toLowerCase().includes(query) ||
        r.attorney_name?.toLowerCase().includes(query) ||
        (Array.isArray(r.managed_by) && r.managed_by.some((m) => m.toLowerCase().includes(query))) ||
        r.status?.toLowerCase().includes(query) ||
        getPaymentStatusSearchText(r.payment_status).includes(query)
      );
    }),
    orderBy,
    order
  ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const {
    userdetails,
    role,
    endorsementDialogOpen,
    setEndorsementDialogOpen,
    selectedBondToEndorse,
    setSelectedBondToEndorse,
    endorsementType,
    setEndorsementType,
    newBondAmount,
    setNewBondAmount,
    endorsementFile,
    setEndorsementFile,
    isSubmittingEndorsement,
    setIsSubmittingEndorsement,
    contactName,
    setContactName,
    contactEmail,
    setContactEmail,
    updatedName,
    setUpdatedName,
    counties,
    endorsementToCounty,
    setEndorsementToCounty,
    loadingCounties,
    estimatedAssets,
    setEstimatedAssets,
    dwbFirstName,
    setDwbFirstName,
    dwbMiddleName,
    setDwbMiddleName,
    dwbLastName,
    setDwbLastName,
    dwbSuffix,
    setDwbSuffix,
    dwbAlias,
    setDwbAlias,
    endorsementPrincipalId,
    setEndorsementPrincipalId,
  } = useBondEndorsementDialogState();

  const { handleReleaseRider, handleSubmitEndorsementRequest } = useBondEndorsementSubmitHandlers({
    setIsSubmittingEndorsement,
    setEndorsementDialogOpen,
    selectedBondToEndorse,
    endorsementType,
    endorsementFile,
    contactName,
    contactEmail,
    newBondAmount,
    estimatedAssets,
    endorsementToCounty,
    updatedName,
    dwbFirstName,
    dwbMiddleName,
    dwbLastName,
    dwbSuffix,
    dwbAlias,
    endorsementPrincipalId,
    role,
    refreshData: fetchInitialData,
  });

  const {
    handleCancelBondSubmit: handleSubmitCancellationRequest,
    handleDeleteClose,
    handleDeleteConfirm,
    handleReviewBond,
  } = useBondCancelDeleteHandlers({
    selectedBondToCancel,
    setCancelDialogOpen,
    cancellationFile,
    setIsCancelling,
    selectedBondToDelete,
    setDeleteDialogOpen,
    setSelectedBondToDelete,
    setIsDeleting,
    refreshData: fetchInitialData,
  });

  const tableColumns = [
    "bond_number",
    "bond_type",
    "bond_sub_category",
    "principals",
    "estate_name",
    "law_firm_name",
    "attorney_name",
    "managed_by",
    "bond_amount",
    "status",
    "created_at",
    "payment_status",
  ];

  const getPaymentStatusChipProps = useCallback((bond) => {
    const key = normalizePaymentStatusKey(bond.payment_status);
    const labels = { paid: "Paid", unpaid: "Unpaid", na: "N/A", unknown: "Unknown" };
    const colorByKey = {
      paid: "success",
      unpaid: "error",
      na: "default",
      unknown: "default",
    };
    return { label: labels[key], color: colorByKey[key] };
  }, []);

  const handleDeleteClick = (bond) => {
    setSelectedBondToDelete(bond);
    setDeleteDialogOpen(true);
  };

  const handleMarkCancellationComplete = async () => {
    if (!selectedBondToCancel) return;

    setIsMarkingCancellationComplete(true);
    try {
      const endpoint = getBondUpdateEndpoint(
        selectedBondToCancel.bond_type,
        selectedBondToCancel.bond_id
      );
      if (!endpoint) throw new Error("Invalid bond type");

      const payload = buildBondStatusPatchPayload(
        selectedBondToCancel.bond_type,
        "Cancelled"
      );

      await apiClient(endpoint, "PATCH", payload);
      toast.success("Bond cancellation marked as completed successfully");
      setCancelDialogOpen(false);
      await fetchInitialData();
    } catch (error) {
      console.error("Error marking cancellation as complete:", error);
      toast.error("Failed to mark cancellation as completed");
    } finally {
      setIsMarkingCancellationComplete(false);
    }
  };

  const handleRevertCancellation = async () => {
    if (!selectedBondToCancel) return;

    setIsMarkingCancellationComplete(true);
    try {
      const endpoint = getBondUpdateEndpoint(
        selectedBondToCancel.bond_type,
        selectedBondToCancel.bond_id
      );
      if (!endpoint) throw new Error("Invalid bond type");

      const payload = buildBondStatusPatchPayload(
        selectedBondToCancel.bond_type,
        "Approved"
      );

      await apiClient(endpoint, "PATCH", payload);
      toast.success("Bond cancellation reverted successfully");
      setCancelDialogOpen(false);
      await fetchInitialData();
    } catch (error) {
      console.error("Error reverting cancellation:", error);
      toast.error("Failed to revert cancellation");
    } finally {
      setIsMarkingCancellationComplete(false);
    }
  };

  const handleMarkToBeCancelled = async () => {
    if (!selectedBondToCancel) return;

    setIsMarkingToBeCancelled(true);
    try {
      const endpoint = getBondUpdateEndpoint(
        selectedBondToCancel.bond_type,
        selectedBondToCancel.bond_id
      );
      if (!endpoint) throw new Error("Invalid bond type");

      const payload = buildBondStatusPatchPayload(
        selectedBondToCancel.bond_type,
        "To Be Cancelled"
      );

      await apiClient(endpoint, "PATCH", payload);
      toast.success("Bond marked as 'To Be Cancelled'");
      setCancelDialogOpen(false);
      await fetchInitialData();
    } catch (error) {
      console.error("Error marking to be cancelled:", error);
      toast.error("Failed to mark bond 'To Be Cancelled'");
    } finally {
      setIsMarkingToBeCancelled(false);
    }
  };

  const handleLibertyBypassConfirm = async () => {
    if (!selectedBondToLiberty) return;
    const bypassedBy = (userdetails?.first_name && userdetails?.last_name)
      ? `${userdetails.first_name} ${userdetails.last_name}`
      : (userdetails?.email || "Unknown");
    setIsMarkingLibertyComplete(true);
    try {
      await apiClient("/liberty-bypass/", "POST", {
        bond_id: selectedBondToLiberty.bond_id,
        bypassed_by: bypassedBy,
      });
      toast.success("Bond marked as Liberty complete. Notification sent.");
      setLibertyConfirmDialogOpen(false);
      setSelectedBondToLiberty(null);
      await fetchInitialData();
    } catch (error) {
      console.error("Liberty bypass error:", error);
      toast.error(error?.response?.data?.detail || error?.message || "Failed to mark Liberty complete");
    } finally {
      setIsMarkingLibertyComplete(false);
    }
  };

  const fetchBypassHistory = async () => {
    setBypassHistoryLoading(true);
    try {
      const data = await apiClient("/liberty-bypass/list/", "GET");
      setBypassHistoryList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch bypass history:", error);
      toast.error("Failed to load bypass history");
      setBypassHistoryList([]);
    } finally {
      setBypassHistoryLoading(false);
    }
  };

  const handleOpenBypassHistory = () => {
    setBypassHistorySearch("");
    setBypassHistoryDialogOpen(true);
    fetchBypassHistory();
  };

  const fetchPaymentOverrideHistory = async () => {
    setPaymentOverrideHistoryLoading(true);
    try {
      const data = await apiClient("/liberty-payment-status/override/list/", "GET");
      setPaymentOverrideHistoryList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch payment override history:", error);
      toast.error("Failed to load payment override history");
      setPaymentOverrideHistoryList([]);
    } finally {
      setPaymentOverrideHistoryLoading(false);
    }
  };

  const handleOpenPaymentOverrideHistory = () => {
    setPaymentOverrideHistorySearch("");
    setPaymentOverrideHistoryOpen(true);
    fetchPaymentOverrideHistory();
  };

  const handleRefreshClick = () => {
    // Manual refresh should reset filters and page
    const cleared = {
      bondNumber: [],
      bondType: [],
      bondSubCategory: [],
      principal: [],
      professionalGuardian: [],
      estate: [],
      lawFirm: [],
      attorney: [],
      internalUser: [],
      status: [],
      payment_status: "",
      startDate: null,
      endDate: null,
    };
    setFilters(cleared);
    setPage(0);
    dispatch(resetArchivesState());
    fetchInitialData(cleared);
  };

  const handleViewBond = (bond) => {
    const type = bond.bond_type
      .toLowerCase()
      .replace(" ", "-");
    window.open(
      `/vermost-${type}-bond-update/${bond.bond_id}`,
      "_blank"
    );
  };

  const handleLibertyIconClick = (bond) => {
    setSelectedBondToLiberty(bond);
    setLibertyConfirmDialogOpen(true);
  };

  const handleCancelIconClick = (bond) => {
    setSelectedBondToCancel(bond);
    setCancellationFile(null);
    setCancelDialogOpen(true);
  };

  const handleEndorseIconClick = (bond) => {
    setSelectedBondToEndorse(bond);
    setEndorsementType("Increase");
    setNewBondAmount("");
    setEndorsementFile(null);
    setUpdatedName("");
    setEndorsementToCounty("");
    setEstimatedAssets("");
    setDwbFirstName("");
    setDwbMiddleName("");
    setDwbLastName("");
    setDwbSuffix("");
    setDwbAlias("");
    setEndorsementDialogOpen(true);
  };

  const handleCancellationFileInputChange = (e) => {
    const file = e.target.files[0];
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (file && !validTypes.includes(file.type)) {
      toast.error("Only PDF or Word documents are allowed.");
      e.target.value = "";
      return;
    }
    setCancellationFile(file);
    toast.success("File selected successfully!");
  };

  const handleEndorsementSupportingFileChange = (e) => {
    const file = e.target.files[0];
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (file && !validTypes.includes(file.type)) {
      toast.error("Only PDF or Word documents are allowed.");
      e.target.value = "";
      return;
    }
    setEndorsementFile(file);
    toast.success("File selected successfully!");
  };

  return {
    allBonds,
    applyFilters,
    attorneys,
    availableEstates,
    availablePrincipals,
    availableSubcategories,
    bondNumberOptionsArchive,
    bondTypes,
    bypassHistoryDialogOpen,
    bypassHistoryList,
    bypassHistoryLoading,
    bypassHistorySearch,
    cancellationFile,
    cancelDialogOpen,
    clearFilters,
    contactEmail,
    contactName,
    counties,
    deleteDialogOpen,
    dispatch,
    dispatchArchiveFilters,
    dwbAlias,
    endorsementPrincipalId,
    setEndorsementPrincipalId,
    dwbFirstName,
    dwbLastName,
    dwbMiddleName,
    dwbSuffix,
    endorsementDialogOpen,
    endorsementFile,
    endorsementToCounty,
    endorsementType,
    estimatedAssets,
    fetchInitialData,
    filteredData,
    filteredRows,
    filterDialogOpen,
    filters,
    getCancellationInstructionsList,
    getEndorsementInstructions,
    getPaymentStatusChipProps,
    handleCancellationFileInputChange,
    handleCancelIconClick,
    handleDeleteClick,
    handleDeleteClose,
    handleDeleteConfirm,
    handleEndorseIconClick,
    handleEndorsementSupportingFileChange,
    handleExport,
    handleLibertyBypassConfirm,
    handleLibertyIconClick,
    handleMarkCancellationComplete,
    handleMarkToBeCancelled,
    handleOpenBypassHistory,
    handleOpenPaymentOverrideHistory,
    handleRefreshClick,
    handleReleaseRider,
    handleRevertCancellation,
    handleReviewBond,
    handleSubmitCancellationRequest,
    handleSort,
    handleSubmitEndorsementRequest,
    handleViewBond,
    internalUsers,
    isCancelling,
    isDeleting,
    isMarkingCancellationComplete,
    isMarkingLibertyComplete,
    isMarkingToBeCancelled,
    isSubmittingEndorsement,
    lawFirms,
    libertyConfirmDialogOpen,
    loading,
    loadingCounties,
    newBondAmount,
    order,
    orderBy,
    page,
    paymentOverrideHistoryList,
    paymentOverrideHistoryLoading,
    paymentOverrideHistoryOpen,
    paymentOverrideHistorySearch,
    principals,
    professionalGuardians,
    role,
    rowsPerPage,
    searchQuery,
    selectedBondToCancel,
    selectedBondToDelete,
    selectedBondToEndorse,
    selectedBondToLiberty,
    setBypassHistoryDialogOpen,
    setBypassHistorySearch,
    setCancellationFile,
    setCancelDialogOpen,
    setContactEmail,
    setContactName,
    setDeleteDialogOpen,
    setDwbAlias,
    setDwbFirstName,
    setDwbLastName,
    setDwbMiddleName,
    setDwbSuffix,
    setEndorsementDialogOpen,
    setEndorsementFile,
    setEndorsementToCounty,
    setEndorsementType,
    setEstimatedAssets,
    setFilterDialogOpen,
    setFilters,
    setLibertyConfirmDialogOpen,
    setNewBondAmount,
    setPage,
    setPaymentOverrideHistoryOpen,
    setPaymentOverrideHistorySearch,
    setRowsPerPage,
    setSelectedBondToCancel,
    setSelectedBondToDelete,
    setSelectedBondToEndorse,
    setSelectedBondToLiberty,
    setUpdatedName,
    statusOptions,
    tableColumns,
    updatedName,
    userdetails,
  };
}

export { normalizePaymentStatusKey } from "../Law Firm User/helpers/lawFirmBondsHelpers";
